import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

const StocktakeReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('summary'); // 'summary' or 'detailed'

  const fetchReport = useCallback(async () => {
    try {
      const response = await api.get(`/stocktakes/${id}/report`);
      setReport(response.data.report);
    } catch (err) {
      console.error('Failed to load report:', err);
      alert('Failed to load report');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatNumber = (num) => {
    return parseFloat(num || 0).toFixed(2);
  };

  const groupEntriesByArea = () => {
    if (!report) return {};
    
    const grouped = {};
    report.entries.forEach(entry => {
      if (!grouped[entry.area_name]) {
        grouped[entry.area_name] = [];
      }
      grouped[entry.area_name].push(entry);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!report) {
    return <div className="container">Report not found</div>;
  }

  const groupedEntries = groupEntriesByArea();

  return (
    <div className="container">
      <div className="card">
        <div className="card-header" style={{ background: 'var(--pink-light)' }}>
          <div>
            <h2 className="card-title">Stocktake Report</h2>
            <div style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
              {report.stocktakeInfo.name}
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>
              Started: {formatDate(report.stocktakeInfo.started_at)}
              {report.stocktakeInfo.completed_at && 
                ` • Completed: ${formatDate(report.stocktakeInfo.completed_at)}`
              }
            </div>
          </div>
          <button className="btn btn-secondary btn-small" onClick={() => navigate('/')}>
            Dashboard
          </button>
        </div>

        <div className="nav-tabs">
          <button
            className={`nav-tab ${viewMode === 'summary' ? 'active' : ''}`}
            onClick={() => setViewMode('summary')}
          >
            Summary
          </button>
          <button
            className={`nav-tab ${viewMode === 'detailed' ? 'active' : ''}`}
            onClick={() => setViewMode('detailed')}
          >
            Detailed by Area
          </button>
        </div>

        {viewMode === 'summary' ? (
          <>
            <h3 className="mb-2">Grand Totals (Raw Materials & Components)</h3>
            {report.grandTotals.length === 0 ? (
              <div className="alert alert-info">No entries recorded</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--pink-light)', borderBottom: '2px solid var(--border)' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Item</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right' }}>Code</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right' }}>Cartons</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right' }}>Sleeves</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right' }}>Units</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right' }}>Total</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right' }}>BOSS Entry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.grandTotals.map((item, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '0.75rem', fontWeight: 600 }}>
                          {item.name}
                          {item.fromPreparedProducts && (
                            <div style={{ 
                              fontSize: '0.75rem', 
                              color: 'var(--text-light)', 
                              fontWeight: 400,
                              marginTop: '0.25rem'
                            }}>
                              Includes {formatNumber(item.fromPreparedProducts.reduce((sum, p) => sum + p.componentUsage, 0))} from prepared products
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', color: 'var(--text-light)' }}>
                          {item.code || '-'}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{item.hasCartons ? formatNumber(item.cartons) : '-'}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{item.hasSleeves ? formatNumber(item.sleeves) : '-'}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{item.hasUnits ? formatNumber(item.units) : '-'}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 700 }}>
                          {formatNumber(item.totalUnits)} <span style={{ fontWeight: 400, color: 'var(--text-light)', fontSize: '0.85rem' }}>{item.unitType}</span>
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600, color: 'var(--pink-dark)' }}>
                          {(item.bossEntryAmount != null && item.bossEntryAmount !== '' && item.bossEntryAmount !== 0) ? formatNumber(item.totalUnits / item.bossEntryAmount) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {report.preparedProducts && report.preparedProducts.length > 0 && (
              <>
                <h3 className="mt-3 mb-2">Prepared Products</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--pink-light)', borderBottom: '2px solid var(--border)' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Product</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Code</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Cartons</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Sleeves</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Units</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Total</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>BOSS Entry</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.preparedProducts.map((item, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '0.75rem', fontWeight: 600 }}>{item.name}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', color: 'var(--text-light)' }}>
                            {item.code || '-'}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{item.hasCartons ? formatNumber(item.cartons) : '-'}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{item.hasSleeves ? formatNumber(item.sleeves) : '-'}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{item.hasUnits ? formatNumber(item.units) : '-'}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 700 }}>
                            {formatNumber(item.totalUnits)} <span style={{ fontWeight: 400, color: 'var(--text-light)', fontSize: '0.85rem' }}>{item.unitType}</span>
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600, color: 'var(--pink-dark)' }}>
                            {(item.bossEntryAmount != null && item.bossEntryAmount !== '' && item.bossEntryAmount !== 0) ? formatNumber(item.totalUnits / item.bossEntryAmount) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {report.preparedProductBreakdown && report.preparedProductBreakdown.length > 0 && (
              <>
                <h3 className="mt-3 mb-2">Component Breakdown</h3>
                <div style={{ background: 'var(--bg-light)', padding: '1rem', borderRadius: '8px' }}>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '1rem' }}>
                    Shows how components from prepared products are calculated (already included in Grand Totals above)
                  </p>
                  {report.preparedProductBreakdown.map((component, index) => (
                    <div key={index} style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                        {component.name} ({component.unitType})
                        <span style={{ float: 'right', color: 'var(--pink)' }}>
                          {formatNumber(component.totalUnits)} total
                        </span>
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>
                        {component.fromPreparedProducts.map((usage, i) => (
                          <div key={i} style={{ marginLeft: '1rem', marginTop: '0.25rem' }}>
                            • {formatNumber(usage.componentUsage)} from {formatNumber(usage.quantity)} × {usage.productName}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <h3 className="mt-3 mb-2">Summary by Area</h3>
            <div>
              {report.areasSummary.map((area, index) => (
                <div key={index} className="list-item">
                  <div className="list-item-content">
                    <div className="list-item-title">{area.area_name}</div>
                    <div className="list-item-subtitle">
                      {area.item_count} items counted
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <h3 className="mb-2">Detailed Breakdown by Area</h3>
            {Object.keys(groupedEntries).length === 0 ? (
              <div className="alert alert-info">No entries recorded</div>
            ) : (
              Object.keys(groupedEntries).map((areaName) => (
                <div key={areaName} className="card" style={{ background: 'var(--bg-light)' }}>
                  <h4 style={{ marginBottom: '1rem', color: 'var(--pink-dark)' }}>{areaName}</h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'var(--white)', borderBottom: '2px solid var(--border)' }}>
                          <th style={{ padding: '0.5rem', textAlign: 'left', fontSize: '0.875rem' }}>Item</th>
                          <th style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.875rem' }}>Cartons</th>
                          <th style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.875rem' }}>Sleeves</th>
                          <th style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.875rem' }}>Units</th>
                          <th style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.875rem' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupedEntries[areaName].map((entry, index) => (
                          <tr key={index} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '0.5rem' }}>
                              <div style={{ fontWeight: 600 }}>{entry.item_name}</div>
                              {entry.item_code && (
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>
                                  #{entry.item_code}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                              {entry.has_cartons ? formatNumber(entry.cartons) : '-'}
                            </td>
                            <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                              {entry.has_sleeves ? formatNumber(entry.sleeves) : '-'}
                            </td>
                            <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                              {entry.has_units ? formatNumber(entry.units) : '-'}
                            </td>
                            <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 700 }}>
                              {formatNumber(entry.total_units)} {entry.unit_type}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        <div className="mt-3 flex gap-2">
          <button 
            className="btn btn-primary"
            onClick={() => window.print()}
          >
            Print Report
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/')}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default StocktakeReport;
